import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptIcon from "@mui/icons-material/Receipt";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import agencyOrdersService from "../../../../../api/agencyOrders/AgencyOrdersService";
import s from "./OrderApprovalSuccess.module.css";

// ========================== | PANTALLA DE CONFIRMACIÓN DE APROBACIÓN | ======================================

export const OrderApprovalSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const orderId = location.pathname.split("/").pop();
  const deliveryNoteId = location.state?.deliveryNoteId;

  useEffect(() => {
    if (location.state?.order) {
      setOrder(location.state.order);
      setLoading(false);
    } else if (orderId) {
      // Si no viene en el state, obtener el pedido
      agencyOrdersService
        .getOrderById(orderId)
        .then((data) => {
          setOrder(data);
        })
        .catch((error) => {
          console.error("Error al obtener pedido:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [orderId, location.state]);

  const handleViewDeliveryNote = () => {
    const noteId = deliveryNoteId || order?.deliveryNoteId;
    const approvalDate = order?.approvalDate || order?.deliveryDate;
    if (noteId) {
      // Navegar a la ruta de detalle y pasar el ID del remito y la fecha de aprobación
      navigate(`/main/remitos/detail`, {
        state: { 
          deliveryNoteId: noteId,
          approvalDate: approvalDate // Pasar la fecha de aprobación para usarla si el remito no tiene date
        }
      });
    }
  };

  const handleBackToList = () => {
    navigate("/main/stock-de-cupones?tab=1");
  };

  if (loading) {
    return (
      <Grid container className={s.container}>
        <Grid item xs={12} className={s.loader}>
          <Typography>Cargando información...</Typography>
        </Grid>
      </Grid>
    );
  }

  if (!order) {
    return (
      <Grid container className={s.container}>
        <Grid item xs={12} sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="error">
            No se pudo cargar la información del pedido
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            sx={{ mt: 2 }}
          >
            Volver al listado
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container className={s.container}>
      <Grid item xs={12} className={s.header}>
        <CheckCircleIcon sx={{ fontSize: "4rem", color: "success.main", mb: 2 }} />
        <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
          ✅ Pedido Aprobado Exitosamente
        </Typography>
        <Typography variant="h5" color="text.secondary">
          Pedido #{order.nroOrder || order.orderId}
        </Typography>
      </Grid>

      <Grid item xs={12} md={8} sx={{ mx: "auto", mt: 4 }}>
        <Card className={s.successCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              El pedido ha sido aprobado correctamente
            </Typography>

            {deliveryNoteId || order.deliveryNoteId ? (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <ReceiptIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
                  <Box>
                    <Typography variant="body1" color="text.secondary">
                      Remito generado automáticamente
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      #{deliveryNoteId || order.deliveryNoteId}
                    </Typography>
                  </Box>
                  <Chip
                    label="Activo"
                    color="success"
                    size="small"
                    sx={{ ml: "auto" }}
                  />
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                El remito se generará próximamente
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Stock transferido a la agencia
            </Typography>

            {order.details && order.details.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {order.details.map((detail, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: "primary.light", borderRadius: 1 }}>
                    <Typography variant="body1">
                      <strong>{detail.game?.name || "-"}</strong> - Sorteo: <strong>#{detail.raffle?.number || "-"}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Cantidad aprobada:{" "}
                      <strong>{detail.quantityApproved?.toLocaleString() || 0} cupones</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No hay detalles disponibles
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 4 }}>
              {deliveryNoteId || order.deliveryNoteId ? (
                <Button
                  variant="contained"
                  startIcon={<ReceiptIcon />}
                  onClick={handleViewDeliveryNote}
                  size="large"
                >
                  Ver Remito
                </Button>
              ) : null}
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToList}
                size="large"
              >
                Volver al Listado
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};


